import styled from '@/styles';
import { dependabilityV2Colors } from '@/styles/colors';
import {
  isWorkerSuspended,
  SUSPENDED_BADGE_LABEL,
} from '@/util/dependabilityBadge';
import { tierColor } from './tierColors';
import { InfoTipIcon } from './icons';

const Pill = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 8px',
  borderRadius: '8px',
  color: '#FFFFFF',
  fontSize: '$sm',
  fontWeight: 700,
});

const InfoButton = styled('button', {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'none',
  border: 'none',
  // GRAVY-10347: the bare icon was a ~14px tap target and hard to hit. Pad the
  // button out to a comfortable ~28px hit area and pull it back with a negative
  // margin so the visible layout (icon position/spacing) is unchanged.
  padding: '7px',
  margin: '-7px',
  minWidth: '28px',
  minHeight: '28px',
  cursor: 'pointer',
  color: '#FFFFFF',
});

// Medal glyph leading the pill (Figma export 37920:11819) — white, so it
// reads on every tier background.
const MedalIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    data-testid="tier-pill-medal"
  >
    <path
      d="M15.1266 4.00024C15.1266 8.2402 13.6066 13.2005 8.16666 15.2004C2.66279 13.1525 1.49951 6.88034 1.60611 4.00024L8.16666 0.800049L15.1266 4.00024ZM7.02994 6.22388L4.06021 6.61548L6.23306 8.67798L5.68716 11.6243L8.32095 10.1956L10.9538 11.6243L10.4088 8.67798L12.5817 6.61548L9.61099 6.22388L8.32095 3.51978L7.02994 6.22388Z"
      fill="currentColor"
    />
  </svg>
);

interface Props {
  tier: string;
  label: string;
  onInfoClick?: () => void;
  // open/closed state of the tier popover this button controls — surfaced as
  // aria-expanded so assistive tech gets the state (fix round 3)
  expanded?: boolean;
  // GRAVY-10404: DISPLAY-ONLY. A suspended / permanently-removed worker shows a
  // dark "Suspended" pill in place of the tier — distinct from Monitored (red)
  // and the no-show caution badge. The score number is untouched.
  accountabilityStatus?: string | null;
}

const TierPill = ({
  tier,
  label,
  onInfoClick,
  expanded = false,
  accountabilityStatus,
}: Props) => {
  const suspended = isWorkerSuspended(accountabilityStatus);
  return (
    <Pill
      css={{
        backgroundColor: suspended
          ? dependabilityV2Colors.depSuspended
          : tierColor(tier),
      }}
      data-testid="tier-pill"
      data-suspended={suspended || undefined}
    >
      {/* The medal implies a tier ranking, which a suspended worker no longer
          holds — so it is dropped when suspended, leaving a plain dark chip. */}
      {!suspended && <MedalIcon />}
      {suspended ? SUSPENDED_BADGE_LABEL : label}
      {onInfoClick && (
        <InfoButton
          type="button"
          aria-label="Dependability tier info"
          aria-haspopup="dialog"
          aria-expanded={expanded}
          // GRAVY-10233: stop the opening click here. TierPopover's shared Popover
          // arms a window-level `useClickout` the moment it opens; without this,
          // the very click that opened the popover keeps bubbling to window, the
          // clickout listener fires, and the popover closes ~1ms after it opened —
          // so a real user click flashed it open then shut ("nothing happens").
          // (Reproduced on live QA; jest/dispatchEvent miss it as they don't bubble
          // the real click to window under the same effect timing.)
          onClick={(e) => {
            e.stopPropagation();
            onInfoClick();
          }}
        >
          <InfoTipIcon />
        </InfoButton>
      )}
    </Pill>
  );
};

export default TierPill;
